import type { Sheet } from "@fortune-sheet/core";

/**
 * A cell's own Excel borders, in the shape Fortune Sheet keeps them: `config.borderInfo` entries of
 * `rangeType: "cell"`. One representation shared by the two renderings of a workbook. The preview's
 * canvas reads it directly (`getBorderInfoComputeRange` in @fortune-sheet/core), and the PDF reads the
 * same entries through `cellBordersOf`. That way a border can't look one way on screen and another way
 * on paper.
 */

/**
 * Fortune Sheet's border style codes, which its canvas renderer looks up in `setLineDash`
 * (@fortune-sheet/core `"1": "Thin" … "13": "Thick"`). Named here because the library exports no names
 * for them.
 */
export const FortuneBorderStyle = {
	Thin: 1,
	Hair: 2,
	Dotted: 3,
	Dashed: 4,
	DashDot: 5,
	DashDotDot: 6,
	Double: 7,
	Medium: 8,
	MediumDashed: 9,
	MediumDashDot: 10,
	MediumDashDotDot: 11,
	SlantDashDot: 12,
	Thick: 13,
} as const;

export type FortuneBorderStyleCode = (typeof FortuneBorderStyle)[keyof typeof FortuneBorderStyle];

const STYLE_CODES: ReadonlySet<number> = new Set(Object.values(FortuneBorderStyle));

/** One drawn side: a style code and a CSS `#RRGGBB` colour, the two fields Fortune Sheet's renderer reads. */
export type BorderSide = { style: FortuneBorderStyleCode; color: string };

/** Fortune Sheet's own side keys: left, right, top, bottom. */
export const BORDER_SIDE_KEYS = ["l", "r", "t", "b"] as const;

export type BorderSideKey = (typeof BORDER_SIDE_KEYS)[number];

/** The sides of one cell that have a line; a side that is absent has none. */
export type CellBorderSides = Partial<Record<BorderSideKey, BorderSide>>;

/** One `config.borderInfo` entry, exactly as @fortune-sheet/core's `rangeType === "cell"` branch reads it. */
export type CellBorderInfo = {
	rangeType: "cell";
	value: { row_index: number; col_index: number } & CellBorderSides;
};

export const borderCellKey = (row: number, column: number): string => `${row}_${column}`;

export const isBorderSide = (value: unknown): value is BorderSide =>
	typeof value === "object" &&
	value !== null &&
	"style" in value &&
	"color" in value &&
	typeof value.style === "number" &&
	STYLE_CODES.has(value.style) &&
	typeof value.color === "string";

/** The sides of a `value` object that hold a valid border. Throws on a side that is present but malformed:
 *  dropping it would print the cell without the line the workbook gave it, with nothing to show why. */
const sidesOf = (value: object): CellBorderSides => {
	const sides: CellBorderSides = {};
	for (const key of BORDER_SIDE_KEYS) {
		if (!(key in value)) continue;
		const side: unknown = Reflect.get(value, key);
		// Fortune Sheet writes `null` for a side it has cleared, so `null` means no line rather than bad data.
		if (side === null || side === undefined) continue;
		if (!isBorderSide(side)) throw new Error(`Malformed ${key} border in borderInfo: ${JSON.stringify(side)}`);
		sides[key] = side;
	}
	return sides;
};

/**
 * Every cell's borders, keyed `"{row}_{col}"`, read back out of a sheet's `borderInfo`.
 *
 * Only `rangeType: "cell"` entries exist, because the converter writes nothing else and the preview
 * toolbar has no border control (`FORMATTING_TOOLBAR_ITEMS`). Any other entry, such as a `"range"` from
 * Fortune Sheet's own border tool, throws. Skipping it would print a page whose lines disagree with the
 * screen, which is what this module exists to prevent.
 */
export const cellBordersOf = (sheet: Sheet): Map<string, CellBorderSides> => {
	// A sheet with no `borderInfo` has no borders at all, and that is what Excel prints for it.
	const entries: unknown[] = sheet.config?.borderInfo ?? [];
	const borders = new Map<string, CellBorderSides>();
	for (const entry of entries) {
		if (typeof entry !== "object" || entry === null || !("rangeType" in entry) || entry.rangeType !== "cell" || !("value" in entry)) {
			throw new Error(`Unsupported borderInfo entry: ${JSON.stringify(entry)}`);
		}
		const { value } = entry;
		if (typeof value !== "object" || value === null || !("row_index" in value) || !("col_index" in value)) {
			throw new Error(`borderInfo cell entry without a position: ${JSON.stringify(entry)}`);
		}
		const { row_index: row, col_index: column } = value;
		if (typeof row !== "number" || typeof column !== "number") throw new Error(`borderInfo cell entry without a position: ${JSON.stringify(entry)}`);
		borders.set(borderCellKey(row, column), sidesOf(value));
	}
	return borders;
};
